import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/app-shell";
import { listImporters } from "@/migration/registry";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import type { ImportReport } from "@/migration/engine/types";
import { ChevronRight, Clock, Upload, FlaskConical, FileText } from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings/import/")({
  head: () => ({
    meta: [
      { title: "Dataimport og migrering | VERQO" },
      { name: "description", content: "Flytt eksisterende data inn i VERQO." },
    ],
  }),
  component: ImportIndex,
});

type JobRow = {
  id: string;
  entity: string;
  source: string;
  file_name: string | null;
  imported_count: number;
  updated_count: number;
  skipped_count: number;
  failed_count: number;
  duplicate_count: number;
  total_rows: number;
  created_at: string;
  status: string;
  dry_run: boolean;
  report: ImportReport | null;
  user_id: string;
  company_id: string;
};

function ImportIndex() {
  const { companyId, companyName } = useActiveCompanyName();
  const importers = listImporters();
  const [selected, setSelected] = useState<JobRow | null>(null);

  const { data: jobs } = useQuery({
    queryKey: ["import-jobs", companyId],
    enabled: !!companyId,
    queryFn: async () => {
      const { data } = await supabase
        .from("import_jobs")
        .select("id,entity,source,file_name,imported_count,updated_count,skipped_count,failed_count,duplicate_count,total_rows,created_at,status,dry_run,report,user_id,company_id")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false })
        .limit(50);
      return (data ?? []) as unknown as JobRow[];
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dataimport og migrering"
        subtitle="Flytt kunder, prosjekter, materialregister og tilbud fra andre systemer inn i VERQO. Alt bruker samme Migration Engine."
      />

      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Hva vil du importere?</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {importers.map((imp) => (
            imp.available ? (
              <Link key={imp.entity} to="/settings/import/$entity" params={{ entity: imp.entity }} className="block group">
                <Card className="p-4 hover:border-primary transition-colors h-full">
                  <div className="flex items-start justify-between gap-2">
                    <Upload className="w-5 h-5 text-primary" />
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="mt-3 font-semibold">{imp.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{imp.description}</div>
                </Card>
              </Link>
            ) : (
              <Card key={imp.entity} className="p-4 border-dashed opacity-70">
                <div className="flex items-start justify-between gap-2">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <Badge variant="secondary" className="text-xs">Kommer</Badge>
                </div>
                <div className="mt-3 font-semibold">{imp.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{imp.description}</div>
              </Card>
            )
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
          <Clock className="w-3 h-3" /> Importhistorikk
        </h2>
        {!jobs || jobs.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">Ingen importer kjørt enda.</Card>
        ) : (
          <Card className="divide-y">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => setSelected(j)}
                className="w-full text-left p-4 flex items-center justify-between gap-3 text-sm hover:bg-accent/50 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{j.file_name ?? j.entity}</span>
                    {j.dry_run && <Badge variant="secondary" className="gap-1 text-[10px]"><FlaskConical className="w-3 h-3" /> Test</Badge>}
                    <Badge variant="outline" className="text-[10px]">{j.entity}</Badge>
                    <Badge variant="outline" className="text-[10px]">{j.source}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(j.created_at).toLocaleString("nb-NO")} · {companyName} · {j.total_rows} rader
                  </div>
                </div>
                <div className="flex gap-3 text-xs shrink-0">
                  <span className="text-primary">{j.imported_count} ny</span>
                  <span className="text-primary">{j.updated_count} oppd.</span>
                  {j.skipped_count > 0 && <span className="text-amber-600">{j.skipped_count} hopp.</span>}
                  {j.failed_count > 0 && <span className="text-destructive">{j.failed_count} feil</span>}
                  <FileText className="w-4 h-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </Card>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Importrapport
              {selected?.dry_run && <Badge variant="secondary" className="gap-1"><FlaskConical className="w-3 h-3" /> Testimport</Badge>}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Dato:</span> {new Date(selected.created_at).toLocaleString("nb-NO")}</div>
                <div><span className="text-muted-foreground">Datatype:</span> {selected.entity}</div>
                <div><span className="text-muted-foreground">Kilde:</span> {selected.source}</div>
                <div><span className="text-muted-foreground">Fil:</span> {selected.file_name ?? "—"}</div>
                <div><span className="text-muted-foreground">Bedrift:</span> {companyName}</div>
                <div><span className="text-muted-foreground">Status:</span> {selected.status}</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Importert" value={selected.imported_count} tone="ok" />
                <Stat label="Oppdatert" value={selected.updated_count} tone="ok" />
                <Stat label="Hoppet over" value={selected.skipped_count} tone="warn" />
                <Stat label="Feilet" value={selected.failed_count} tone="err" />
              </div>
              {selected.report?.errors && selected.report.errors.length > 0 && (
                <div>
                  <div className="font-medium mb-2">Feilrader</div>
                  <div className="border rounded max-h-64 overflow-auto text-xs divide-y">
                    {selected.report.errors.map((e) => (
                      <div key={e.index} className="p-2">
                        <span className="font-mono text-muted-foreground">rad {e.index}:</span> {e.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button variant="outline" asChild>
                  <Link to="/settings/import/$entity" params={{ entity: selected.entity }}>Ny import av samme type</Link>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: "ok" | "warn" | "err" }) {
  const color = tone === "ok" ? "text-primary" : tone === "warn" ? "text-amber-600" : "text-destructive";
  return (
    <div className="border rounded p-2 text-center">
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function useActiveCompanyName() {
  const { companyId, company } = useActiveCompany();
  return { companyId, companyName: company?.companyName ?? "" };
}
